import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Infobulle } from './Infobulle';

export interface ActionMenu {
  cle: string;
  libelle: string;
  /** Une ligne sous le libelle : ce que l'action fait, en clair. */
  aide?: string;
  icone: ReactNode;
  onChoisir: () => void;
}

/**
 * Menu d'actions d'une modale, pose a gauche de la croix.
 *
 * TROIS POINTS QUI NE VONT PAS DE SOI :
 *
 * 1. `⋮` et non `☰`. Les trois barres servent deja a la navigation
 *    mobile du bandeau ; les reprendre ici pour un autre sens
 *    brouillerait les deux. Les trois points verticaux sont la
 *    convention d'un menu d'actions attache a un objet.
 *
 * 2. `Escape` ferme LE MENU, pas la modale. La modale ecoute cette
 *    touche sur `document` : sans `stopPropagation`, une seule pression
 *    refermerait les deux, et l'utilisateur perdrait sa saisie pour
 *    avoir voulu refermer un menu.
 *
 * 3. Un menu VIDE ne s'affiche pas du tout. Les entrees dependent des
 *    permissions ; un bouton qui n'ouvre rien est pire qu'un bouton
 *    absent.
 */
export function MenuActions({ actions }: { actions: ActionMenu[] }) {
  const [ouvert, setOuvert] = useState(false);
  const conteneur = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ouvert) return;
    const auClic = (evenement: MouseEvent) => {
      if (!conteneur.current?.contains(evenement.target as Node)) setOuvert(false);
    };
    const auClavier = (evenement: KeyboardEvent) => {
      if (evenement.key !== 'Escape') return;
      // La modale ecoute Escape sur `document` : sans cet arret, elle se
      // refermerait en meme temps que le menu.
      evenement.stopPropagation();
      setOuvert(false);
    };
    document.addEventListener('mousedown', auClic);
    document.addEventListener('keydown', auClavier, true);
    return () => {
      document.removeEventListener('mousedown', auClic);
      document.removeEventListener('keydown', auClavier, true);
    };
  }, [ouvert]);

  if (actions.length === 0) return null;

  return (
    <div className="relative" ref={conteneur}>
      <Infobulle texte={ouvert ? null : 'Actions'} className="inline-flex">
      <button
        type="button"
        aria-label="Actions"
        aria-expanded={ouvert}
        aria-haspopup="menu"
        className={`rounded p-1 text-encre-400 hover:bg-encre-100 hover:text-encre-700 ${
          ouvert ? 'bg-encre-100 text-encre-700' : ''
        }`}
        onClick={() => setOuvert((o) => !o)}
      >
        <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden>
          <circle cx="8" cy="3" r="1.4" />
          <circle cx="8" cy="8" r="1.4" />
          <circle cx="8" cy="13" r="1.4" />
        </svg>
      </button>
      </Infobulle>

      {ouvert && (
        <div
          role="menu"
          // Ancre a DROITE : le menu est en fin d'en-tete, s'ouvrir vers
          // la droite le ferait sortir du panneau.
          className="absolute right-0 z-20 mt-1 w-72 rounded-md border border-encre-200 bg-white p-1 shadow-lg"
        >
          {actions.map((action) => (
            <button
              key={action.cle}
              type="button"
              role="menuitem"
              className="flex w-full items-start gap-2.5 rounded px-2.5 py-2 text-left hover:bg-encre-100"
              onClick={() => {
                setOuvert(false);
                action.onChoisir();
              }}
            >
              <span className="mt-0.5 shrink-0 text-encre-400">{action.icone}</span>
              <span className="min-w-0">
                <span className="block text-sm text-encre-800">{action.libelle}</span>
                {action.aide && (
                  <span className="block text-xs text-encre-500">{action.aide}</span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
